// Auth integration layer for encore-dashboard
// Connects Auth0 authentication with the network layer

import { useAuth0 } from '../../auth';
import { useEffect, useMemo, useState } from 'react';
import {
  isRunningInWebView,
  subscribeToNativeAuth,
  requestTokenFromNative,
} from './nativeBridge';
import { useClientId } from '../../hooks/useClientId';

export interface AuthIntegrationState {
  isAuthenticated: boolean;
  isNetworkAuthReady: boolean;
  token: string | null;
}

export function useAuthIntegration(): AuthIntegrationState {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [token, setToken] = useState<string | null>(null);
  const [isNetworkAuthReady, setIsNetworkAuthReady] = useState(false);
  const clientId = useClientId();

  const inWebView = useMemo(() => isRunningInWebView(), []);

  // WebView/native token flow
  useEffect(() => {
    if (!inWebView) {
      return;
    }

    const unsubscribe = subscribeToNativeAuth(message => {
      if (message.type === 'auth-token' || message.type === 'auth-refresh') {
        const newToken = message.accessToken;
        setToken(newToken);
        setIsNetworkAuthReady(true);
      } else if (message.type === 'auth-logout') {
        setToken(null);
        setIsNetworkAuthReady(false);
      } else {
        // Ignore other message types
      }
    });

    // Ask native for current token on mount
    requestTokenFromNative();

    return unsubscribe;
  }, [inWebView]);

  // Browser/Auth0 token flow
  useEffect(() => {
    if (inWebView) {
      return; // Skip Auth0 flow when running inside native webview
    }

    if (isAuthenticated) {
      const setupAuth = async () => {
        try {
          const authorizationParams: Record<string, string> = {};
          if (clientId) {
            authorizationParams.l_client_id = clientId;
          }
          const accessToken = await getAccessTokenSilently({
            authorizationParams,
          });
          setToken(accessToken);

          setIsNetworkAuthReady(true);
        } catch {
          setIsNetworkAuthReady(false);
        }
      };

      setupAuth();
    } else {
      setToken(null);
      setIsNetworkAuthReady(false);
    }
  }, [inWebView, isAuthenticated, getAccessTokenSilently, clientId]);

  return {
    isAuthenticated: inWebView ? Boolean(token) : isAuthenticated,
    isNetworkAuthReady,
    token,
  };
}
